/**
 * Resources Types
 * Unified resource management data structures for artifacts, library, and temp files
 */

export type ResourceType = 'artifact' | 'library' | 'temp';

/**
 * Artifact field value (for multi-field artifacts)
 */
export interface ArtifactFieldValue {
  key: string;
  value: unknown;  // Direct value for metadata, filename for media
  is_media: boolean;
  url?: string;  // Presigned URL for media fields
  content_type?: string;
}

/**
 * Unified resource information
 */
export interface ResourceInfo {
  id: string;  // Format: "type:id"
  resource_type: ResourceType;
  user_id: string;
  name: string;  // Filename or title
  content_type: string;
  size: number;  // Size in bytes
  created_at: string;  // ISO timestamp
  url?: string;  // Presigned access URL
  is_current?: boolean;  // For artifacts only
  is_expired?: boolean;  // For artifacts only
  subtype?: string;  // artifact_type or category
  metadata: Record<string, unknown>;
  // Multi-field artifacts: contains all field values
  version?: string;  // Artifact version (YYYYMMDD, etc.)
  valid_from?: string;  // Scheduled validity start (ISO), for batch/scheduled artifacts
  fields?: Record<string, ArtifactFieldValue>;
}

/**
 * Summary for a single resource type
 */
export interface ResourceTypeSummary {
  count: number;
  size_bytes: number;
  by_subtype: Record<string, number>;
}

/**
 * Complete resource summary for a user
 */
export interface UserResourceSummary {
  user_id: string;
  artifacts: ResourceTypeSummary;
  library: ResourceTypeSummary;
  temp: ResourceTypeSummary;
  total_count: number;
  total_size_bytes: number;
}

/**
 * Paginated resource list response
 */
export interface ResourceListResponse {
  resources: ResourceInfo[];
  total: number;
  has_more: boolean;
}

/**
 * Statistics for a resource type (admin)
 */
export interface ResourceTypeStats {
  total_files: number;
  total_size_bytes: number;
  unique_users: number;
  by_subtype: Record<string, number>;
}

/**
 * Storage usage for a single user (admin)
 */
export interface UserStorageInfo {
  user_id: string;
  total_files: number;
  total_size_bytes: number;
  artifacts_count: number;
  library_count: number;
  temp_count: number;
}

/**
 * System-wide resource statistics (admin)
 */
export interface SystemResourceStats {
  timestamp: string;
  total_files: number;
  total_size_bytes: number;
  artifacts: ResourceTypeStats;
  library: ResourceTypeStats;
  temp: ResourceTypeStats;
  unique_users: number;
  largest_users: UserStorageInfo[];
}

/**
 * Cleanup result
 */
export interface CleanupResult {
  deleted_count: number;
  deleted_size_bytes: number;
  errors: string[];
}

/**
 * Delete all result
 */
export interface DeleteAllResult {
  user_id: string;
  dry_run: boolean;
  artifacts: {
    deleted: number;
    errors: string[];
  };
  library: {
    deleted: number;
    errors: string[];
  };
  temp: {
    deleted: number;
    errors: string[];
  };
}

/**
 * Request parameters for listing resources
 */
export interface ResourceListParams {
  resource_type?: ResourceType;
  subtype?: string;
  include_urls?: boolean;
  limit?: number;
  offset?: number;
}

/**
 * Request for cleanup operations
 */
export interface CleanupRequest {
  days_old?: number;
  dry_run?: boolean;
}

/**
 * Request to delete all user resources
 */
export interface DeleteAllRequest {
  resource_type?: ResourceType;
  dry_run?: boolean;
}

/**
 * Helper function to format file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Helper function to get resource type label
 */
export function getResourceTypeLabel(type: ResourceType, locale: 'zh' | 'en' = 'zh'): string {
  const labels: Record<ResourceType, { zh: string; en: string }> = {
    artifact: { zh: '产物', en: 'Artifacts' },
    library: { zh: '图库', en: 'Library' },
    temp: { zh: '临时文件', en: 'Temp Files' },
  };
  return labels[type]?.[locale] || type;
}

/**
 * Helper to parse unified resource ID
 */
export function parseResourceId(id: string): { type: ResourceType; localId: string } | null {
  const parts = id.split(':');
  if (parts.length !== 2) return null;
  const [type, localId] = parts;
  if (!['artifact', 'library', 'temp'].includes(type)) return null;
  return { type: type as ResourceType, localId };
}
